/**
 * AssetUploadModal - modal for uploading a new brand asset.
 *
 * Lets the user pick a category (Round / Freestyle), select tags,
 * choose a file, and upload it.
 */

import React, { useState, useRef } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { UploadIcon } from "@/components/icons";
import { TagEditor } from "./TagEditor";
import { useUploadAsset } from "../hooks/useClientAssets";
import type { AssetCategory } from "../types";

interface AssetUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  onUploadComplete: () => void;
}

export const AssetUploadModal: React.FC<AssetUploadModalProps> = ({
  isOpen,
  onClose,
  clientId,
  onUploadComplete,
}) => {
  const [category, setCategory] = useState<AssetCategory>("round");
  const [tags, setTags] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadAsset();

  const resetForm = () => {
    setCategory("round");
    setTags([]);
    setFile(null);
    setDragActive(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      await uploadMutation.mutateAsync({
        clientId,
        file,
        category,
        tags,
      });
      resetForm();
      onUploadComplete();
    } catch (err) {
      console.error("Asset upload failed:", err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type.startsWith("image/")) {
      setFile(dropped);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        onClick={handleUpload}
        disabled={!file}
        isLoading={uploadMutation.isPending}
      >
        Upload
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Brand Asset"
      size="lg"
      footer={footer}
    >
      <div className="p-6 space-y-5">
        {/* Category selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCategory("round")}
              className={`
                flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  category === "round"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
              `}
            >
              Round
            </button>
            <button
              type="button"
              onClick={() => setCategory("freestyle")}
              className={`
                flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${
                  category === "freestyle"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
              `}
            >
              Freestyle
            </button>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <TagEditor selectedTags={tags} onChange={setTags} />
        </div>

        {/* File picker / drop zone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            File
          </label>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg
              cursor-pointer transition-colors
              ${
                dragActive
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }
            `}
          >
            <UploadIcon className="w-8 h-8 text-gray-400 mb-2" />
            {file ? (
              <p className="text-sm text-gray-700 font-medium">{file.name}</p>
            ) : (
              <p className="text-sm text-gray-500">
                Click or drag an image here
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Upload error */}
        {uploadMutation.isError && (
          <p className="text-sm text-red-600">
            Upload failed. Please try again.
          </p>
        )}
      </div>
    </Modal>
  );
};
